import { yupResolver } from '@hookform/resolvers/yup';
import { FormHelperText } from '@mui/material';
import queryString from 'query-string';
import { useEffect } from 'react';
import { analytics } from '../../libs/firebase';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import Button from '../../components/Button';
import Select from '../../components/Select';
import { TABS } from '../../consts';
import { ISeatInfo, useSystemContext } from '../../context/SystemContext/SystemContext';
import { getItemWithExpiry } from '../../utils';

interface SeatInfoFormProps {
  editable?: boolean;
  onSubmit: (data: SeatInfoFormData) => void;
  isConfirmed?: boolean;
  confirmError?: boolean;
  onConfirmChange?: (checked: boolean) => void;
}

export type SeatInfoFormData = Omit<ISeatInfo, 'isConfirmed' | 'id' | 'meny_order' | 'pre_order' | 'special_order'>;

const SeatInfoForm: React.FC<SeatInfoFormProps> = ({ editable = true, onSubmit, isConfirmed = false, confirmError = false, onConfirmChange }) => {
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = queryString.parse(location.search);

  const {
    seatInfo,
    seatFormOptions,
    isLoadingSeat,
    fetchAreasBySeatType,
    fetchAislesBySeatTypeAndArea,
    fetchAdditionalBySeatTypeAreaAndAisle,
    fetchRowsBySeatTypeAreaAisleAndAdditional,
    fetchSeatNumbersBySeatTypeAreaAisleAdditionalAndRow,
    isPreOrderWindowForDate,
    getGameDayPreorder,
  } = useSystemContext();

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^0\d{9,10}$/;
  const isPreOrderDate = (orderDate?: string) => {
    if (!orderDate) return false;

    try {
      return getGameDayPreorder(orderDate) && isPreOrderWindowForDate(orderDate);
    } catch {
      return false;
    }
  };

  const schema = yup.object().shape({
    seat_type: yup.string().required(t('SEAT_TYPE_REQUIRED')),
    area: yup.string().required(t('AREA_REQUIRED')),
    aisle: yup.string().required(t('AISLE_REQUIRED')),
    block: yup
      .string()
      .defined('ADDITIONAL_INFO_REQUIRED')
      .test('block-required', t('ADDITIONAL_INFO_REQUIRED'), function (value) {
        if (!seatFormOptions?.additionalInfoOptions.length) {
          return false;
        }
        if (seatFormOptions.additionalInfoOptions.find((opt) => opt.value === '')) {
          return true;
        }

        return !!value;
      }),
    row: yup.string().required(t('ROW_REQUIRED')),
    seat_number: yup.string().required(t('SEAT_NUMBER_REQUIRED')),
    // additional
    order_date: yup.string().required(t('DATE_REQUIRED')),
    email: yup
      .string()
      .required(t('EMAIL_REQUIRED'))
      .test('email', t('EMAIL_INVALID'), function (value) {
        return !!value && emailRegex.test(value);
      }),
    phone: yup.string().defined().test('phone-required-for-preorder', t('PHONE_INVALID'), function (value) {
      if (!isPreOrderDate(this.parent.order_date)) return true;

      if (!value) {
        return this.createError({ message: t('PHONE_REQUIRED') });
      }

      const cleanedValue = value.replace(/-/g, '');
      return phoneRegex.test(cleanedValue);
    }),
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<SeatInfoFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      order_date: '',
      seat_type: '',
      area: '',
      aisle: '',
      block: '',
      row: '',
      seat_number: '',
      email: getItemWithExpiry('user_email') || '',
      phone: getItemWithExpiry('user_phone') || '',
    },
  });

  const watchedOrderDate = useWatch({ control, name: 'order_date' });
  const shouldCollectPhone = isPreOrderDate(watchedOrderDate);
  const watchedSeatType = useWatch({ control, name: 'seat_type' });
  const watchedArea = useWatch({ control, name: 'area' });
  const watchedAisle = useWatch({ control, name: 'aisle' });
  const watchedBlock = useWatch({ control, name: 'block' });
  const watchedRow = useWatch({ control, name: 'row' });

  useEffect(() => {
    if (seatInfo && seatInfo.id) {
      const storedEmail = getItemWithExpiry('user_email') || '';
      const storedPhone = getItemWithExpiry('user_phone') || '';
      reset({
        ...seatInfo,
        email: seatInfo.email || storedEmail,
        phone: seatInfo.phone || storedPhone,
      });
    }
  }, [seatInfo]);

  useEffect(() => {
    if (!sessionStorage.getItem('visit-first-view-tracked') && typeof window !== 'undefined' && analytics) {
      try {
        analytics.logEvent('visit_first_page_view', {
          path: window.location.pathname,
          locale: i18n?.language || null,
        });
        sessionStorage.setItem('visit-first-view-tracked', '1');
      } catch (e) {
        // ignore analytics errors
      }
    }
  }, []);

  useEffect(() => {
    if (seatFormOptions.orderDateOptions.length > 0) {
      const firstAvailableDate = seatFormOptions.orderDateOptions.find((option) => !option.disabled);
      if (firstAvailableDate && !seatInfo?.order_date) {
        setValue('order_date', firstAvailableDate.value);
      }
    }
  }, [seatFormOptions.orderDateOptions, seatInfo?.order_date]);

  useEffect(() => {
    if (watchedSeatType) {
      fetchAreasBySeatType(watchedSeatType);
    }
  }, [watchedSeatType]);

  useEffect(() => {
    if (watchedSeatType && watchedArea !== undefined) {
      fetchAislesBySeatTypeAndArea(watchedSeatType, watchedArea);
    }
  }, [watchedArea]);

  useEffect(() => {
    if (watchedSeatType && watchedArea !== undefined && watchedAisle !== undefined) {
      fetchAdditionalBySeatTypeAreaAndAisle(watchedSeatType, watchedArea, watchedAisle);
    }
  }, [watchedAisle]);

  useEffect(() => {
    if (watchedSeatType && watchedArea !== undefined && watchedAisle !== undefined && watchedBlock !== undefined) {
      fetchRowsBySeatTypeAreaAisleAndAdditional(watchedSeatType, watchedArea, watchedAisle, watchedBlock);
    }
  }, [watchedBlock, watchedAisle]);

  useEffect(() => {
    if (watchedSeatType && watchedArea !== undefined && watchedAisle !== undefined && watchedBlock !== undefined && watchedRow) {
      fetchSeatNumbersBySeatTypeAreaAisleAdditionalAndRow(watchedSeatType, watchedArea, watchedAisle, watchedBlock, watchedRow);
    }
  }, [watchedRow]);

  const handleFormSubmit = (data: SeatInfoFormData) => {
    onSubmit({ ...data, phone: shouldCollectPhone ? data.phone : '' });
  };

  const onChangeData = () => {
    const seatTabLink = queryString.stringifyUrl({
      url: `/neworder`,
      query: { ...queryParams, tab: TABS.SEAT },
    });

    navigate(seatTabLink);
  };

  const selectedAdditional = seatFormOptions.additionalInfoOptions.find((opt) => opt.value === '') ? true : watchedBlock !== undefined;

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-5">
      {/* Order Date */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('ORDER_DATE')}</label>
        <Controller
          name="order_date"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={seatFormOptions.orderDateOptions}
              error={!!errors.order_date}
              helperText={errors.order_date?.message}
              disabled={isLoadingSeat || !editable}
              placeholder={t('ORDER_DATE_PLACEHOLDER')}
            />
          )}
        />
      </div>

      {/* 1. Seat Type & 2. Area */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          <b>1- </b>
          {t('SEAT_TYPE')}
        </label>
        <Controller
          name="seat_type"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              onChange={(e) => {
                setValue('area', '');
                setValue('aisle', '');
                setValue('block', '');
                setValue('row', '');
                setValue('seat_number', '');
                field.onChange(e);
              }}
              options={seatFormOptions.seatTypeOptions}
              error={!!errors.seat_type}
              helperText={errors.seat_type?.message}
              disabled={isLoadingSeat || !editable}
              placeholder={t('SEAT_TYPE_PLACEHOLDER')}
            />
          )}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          <b>2- </b>
          {t('AREA_NAME')}
        </label>
        <Controller
          name="area"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              onChange={(e) => {
                setValue('aisle', '');
                setValue('block', '');
                setValue('row', '');
                setValue('seat_number', '');
                field.onChange(e);
              }}
              options={seatFormOptions.areaOptions}
              error={!!errors.area}
              helperText={errors.area?.message}
              disabled={isLoadingSeat || !editable || !watchedSeatType}
              placeholder={t('AREA_PLACEHOLDER')}
            />
          )}
        />
      </div>

      {/* 3. Aisle & 4. Additional Info */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          <b>3- </b>
          {t('AISLE')}
        </label>
        <Controller
          name="aisle"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              onChange={(e) => {
                setValue('block', '');
                setValue('row', '');
                setValue('seat_number', '');
                field.onChange(e);
              }}
              options={seatFormOptions.aisleOptions}
              error={!!errors.aisle}
              helperText={errors.aisle?.message}
              disabled={isLoadingSeat || !editable || !watchedArea}
              placeholder={t('AISLE_PLACEHOLDER')}
            />
          )}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          <b>4- </b>
          {t('ADDITIONAL_INFO')}
        </label>
        <Controller
          name="block"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              onChange={(e) => {
                setValue('row', '');
                setValue('seat_number', '');
                field.onChange(e);
              }}
              options={seatFormOptions.additionalInfoOptions}
              error={!!errors.block}
              helperText={errors.block?.message}
              disabled={isLoadingSeat || !editable || !watchedAisle}
              placeholder={t('ADDITIONAL_INFO_PLACEHOLDER')}
            />
          )}
        />
      </div>

      {/* 5. Row & 6. Seat Number */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            <b>5- </b>
            {t('ROW')}
          </label>
          <Controller
            name="row"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                onChange={(e) => {
                  setValue('seat_number', '');
                  field.onChange(e);
                }}
                options={seatFormOptions.rowOptions}
                error={!!errors.row}
                helperText={errors.row?.message}
                disabled={isLoadingSeat || !editable || !selectedAdditional}
                placeholder={t('ROW_PLACEHOLDER')}
              />
            )}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            <b>6- </b>
            {t('SEAT_NUMBER')}
          </label>
          <Controller
            name="seat_number"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                options={seatFormOptions.seatNumberOptions}
                error={!!errors.seat_number}
                helperText={errors.seat_number?.message}
                disabled={isLoadingSeat || !editable || !watchedRow}
                placeholder={t('SEAT_NUMBER_PLACEHOLDER')}
              />
            )}
          />
        </div>
      </div>

      {/* Email Address */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('EMAIL')}</label>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              placeholder={t('EMAIL_PLACEHOLDER')}
              disabled={isLoadingSeat || !editable}
              className="w-full box-border p-[10px] text-base leading-[22px] border border-[#D0D5DD] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#3D9F4A] hover:border-[#9ca3af] disabled:bg-[#f3f4f6] disabled:text-[#9ca3af] disabled:border-[#e5e7eb] disabled:cursor-not-allowed cursor-pointer disabled:placeholder:text-[#9ca3af]"
            />
          )}
        />
        <FormHelperText sx={{ marginTop: '3px' }} className="text-[#475467]">
          {t('EMAIL_INPUT_HELPER_TEXT')}
        </FormHelperText>
        {errors.email && (
          <FormHelperText error sx={{ margin: '3px 14px 0px' }}>
            {errors.email.message}
          </FormHelperText>
        )}
      </div>

      {/* Phone Number */}
      {shouldCollectPhone && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('PHONE')}</label>
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                placeholder={t('PHONE_PLACEHOLDER')}
                disabled={isLoadingSeat || !editable}
                className="w-full box-border p-[10px] text-base leading-[22px] border border-[#D0D5DD] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#3D9F4A] hover:border-[#9ca3af] disabled:bg-[#f3f4f6] disabled:text-[#9ca3af] disabled:border-[#e5e7eb] disabled:cursor-not-allowed cursor-pointer disabled:placeholder:text-[#9ca3af]"
              />
            )}
          />
          <FormHelperText sx={{ marginTop: '3px' }} className="text-[#475467]">
            {t('PRE_ORDER_PHONE_INPUT_HELPER_TEXT')}
          </FormHelperText>
          {errors.phone && (
            <FormHelperText error sx={{ margin: '3px 14px 0px' }}>
              {errors.phone.message}
            </FormHelperText>
          )}
        </div>
      )}

      {/* Confirm Checkbox */}
      {editable && onConfirmChange && (
        <div className="pt-2">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isConfirmed}
              onChange={(e) => onConfirmChange(e.target.checked)}
              className={`h-4 w-4 text-green-600 rounded focus:ring-[#3D9F4A] hover:border-[#9ca3af] ${confirmError ? 'border-red-500' : 'border-[#D0D5DD]'}`}
            />
            <p className={`text-sm font-medium ${confirmError ? 'text-red-600' : 'text-gray-900'}`}>{t('SEAT_INFO_SAVE_DATA')}</p>
          </label>
          <div className="mt-2 text-sm text-gray-600 space-y-1">
            <p className="font-semibold">{t('SEAT_INFO_IMPORTANT')}</p>
            <p>{t('SEAT_INFO_DELETE_DATA_INFO_1')}</p>
          </div>
        </div>
      )}

      {/* Submit Button */}
      {editable ? (
        <div className="pt-6">
          <Button type="submit">{t('CONFIRM')}</Button>
        </div>
      ) : (
        <div className="pt-6">
          <Button type="light" onClick={onChangeData}>
            {t('CHANGE_SEAT_INFO')}
          </Button>
        </div>
      )}
    </form>
  );
};

export default SeatInfoForm;
